import React, { useState, useEffect, useCallback } from 'react';
import { addTotalIssuesFieldToEachTrip, eachResult, formatFlightTimes, getSearchParams, isSort as isSorts, loadTripsForm, toSearchParams, validInt } from '../../js/lib/sharedSearchService';
import { TOO_MANY_SEARCH_RESULTS } from '../../js/defineTdm';
import { collectGarbage, removeAlert, removeAllAlerts, setHelpPage, setWindowTitle } from '../../js/configTdm';
import _ from 'lodash';
import { searchTripsService } from '../../js/trips/tripsServices';
import store from '../../js/redux/store';
import Accordion, { AccordionPanelProps } from "../../js/components/accordion/Accordion";
import TripsTemplate from './TripsTemplate';
import TripsListItem from '../../js/trips/TripsListItem';
import { paramsObject } from '../../js/issues/issueService';
import { ErrorState } from '../../js/issues/types';
import FullPageLoadingSpinner from '../../js/pages/components/fullPageLoadingSpinner';

const Trips: React.FC = () => {
  const [title, setTitle] = useState('SEARCH TRIPS');
  const [totalTripsFound, setTotalTripsFound] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(100);
  const [sort, setSort] = useState('record');
  const [sortAsc, setSortAsc] = useState(true);
  const [trips, setTrips] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [lastSearchStatus, setLastSearchStatus] = useState<number | null>(null);
  const [searchWaiverName, setSearchWaiverName] = useState<string | null>(null);
  const [routeParams, setRouteParams] = useState<{ [key: string]: string }>(paramsObject());
  const [error, setError] = useState<ErrorState | null>(null);
  const [waiver, setWaiver] = useState<{id: string, description: string}>({ id: '', description: '' });
  const [loading, setLoading] = useState<boolean>(true); 
  const [openAccordion, setOpenAccordion] = useState<boolean>(true);

  const hasTrips = () => totalTripsFound > 0;
  const hasMoreTrips = () => offset + limit < totalTripsFound;
  const isSort = (sortCriteria: string, order?: string) => {
    return isSorts({ sort, sortAsc }, sortCriteria, order);
  };

  const noResultsMessage = () => {
    if (lastSearchStatus === 422) {
      return TOO_MANY_SEARCH_RESULTS;
    }
    return 'No trips found';
  };

  useEffect(() => {
    setWindowTitle('Search Trips');
    setHelpPage('helpTripSearch');
    searchApply();
      // Simulate initial loading delay
      const initialLoadTimer = setTimeout(() => {
        setLoading(false);
      }, 1000); // Adjust the delay as needed
    return () => {
      setTrips([]);
      collectGarbage();
    }
  }, []);

  useEffect(() => {
    if ('q' in routeParams) {
      setOpenAccordion(false);
    }
  }, [routeParams]);

  const searchApply = () => {
    const urlParams = getSearchParams(paramsObject());
    if (urlParams.length) {
      if (!routeParams.sortBy) {
        urlParams.push(`sortBy=${sort}`);
        urlParams.push(`order=${sortAsc ? 'ascending' : 'descending'}`); 
      }
      search(urlParams.join('&'));
    } else {
      setTrips([]);
      setTotalTripsFound(0);
      setSort('record');
    }
  }

  const search = useCallback((params: string, append?: boolean) => {
    if (searching) return;

    setSearching(true);
    setSearchWaiverName(null);
    removeAllAlerts();

    try {
      searchTripsService().fetch(params).then((resp) => {
        if (resp?.status != null) {
          setLastSearchStatus(resp.status);
          if (resp.status === 200) {
            if ('data' in resp) {
              if (resp?.data?.trips?.length) {
                const newTrips = eachResult(resp.data.trips, [
                  addTotalIssuesFieldToEachTrip,
                  formatFlightTimes,
                ]);
                setTrips(append ? trips.concat(newTrips) : newTrips);
              }
              if (resp.data?.totalTripsFound != null) {
                setTotalTripsFound(validInt(resp.data.totalTripsFound));
              }
              if (resp.data?.offset != null) {
                setOffset(validInt(resp.data.offset));
              }
              if (resp.data?.limit != null) {
                setLimit(validInt(resp.data.limit));
              }
              if (waiver?.id) {
                setSearchWaiverName(waiver.description);
              }
            }
          }
        }
        setSearching(false);
      });
    } catch (error) {
      setSearching(false);
    }
  }, [searching, trips]);

  const showMoreTrips = () => {
    if (!searching) {
      setOffset(prevOffset => {
        const newOffset = prevOffset + limit;
        if (newOffset < totalTripsFound) {
          let routeParams = { ...paramsObject(), offset: newOffset.toString(), limit: limit.toString(), sortBy: sort, order: sortAsc ? 'ascending' : 'descending'};
          const urlParams = getSearchParams(routeParams);
          if (urlParams.length) {
            search(urlParams.join('&'), true);
          }
        } else {
          return totalTripsFound;
        }
        return newOffset;
      });
    }
  };

  const sortBy = (criteria: string) => {
    if (!hasTrips()) {
      return;
    }

    const form = document.querySelector('[name=searchform]');
    if (form && form.classList.contains('ng-dirty')) {
      loadTripsForm(routeParams);  
    }

    const column = sort;
    let selectedSort = sort;
    let selectedSortAsc = sortAsc;

    if (criteria === column) {
      selectedSortAsc = !sortAsc;
    } else {
      selectedSort = criteria;
      selectedSortAsc = true;
    }

    setOffset(0);
    setTrips([]);
    setTotalTripsFound(0);
    setLimit(100);
    setSort(selectedSort);
    setSortAsc(selectedSortAsc);

    const urlParams = [
      `sortBy=${criteria}`,
      `offset=0`,
      `order=${selectedSortAsc ? 'ascending' : 'descending'}`,
    ];
    Object.entries({ ...routeParams, ...paramsObject() }).forEach(([key, value]) => {
      if (!['sortby', 'order', 'offset'].includes(key.toLowerCase())) {
        urlParams.push(`${key}=${value}`);
      }
    });
    search(urlParams.join('&'));
  };

  let headerLists = [
    {keyName: 'record', displayName: 'Rec. Loc.'},
    {keyName: 'group', displayName: 'Trip Group'},
    {keyName: 'passenger', displayName: 'Passenger Name(s)'},
    {keyName: '', displayName: '', template: <th className="no-sort">Email Address(es)</th>},
    {keyName: 'flight', displayName: 'Flight'},
    {keyName: 'time', displayName: 'Date'},
    {keyName: '', displayName: '', template: <th className="no-sort">
      <div className="sprites sprite-dep-ivory"></div>
    </th>},
    {keyName: '', displayName: '', template: <th className="no-sort">
      <div className="sprites sprite-arr-ivory"></div>
    </th>},
    {keyName: 'arrival', displayName: 'Airports'}
  ];

  return (
    <div className="row">
      {loading && <FullPageLoadingSpinner />} {/* Conditionally render the spinner */}
      <div className="row search-title">
        <div className="col-md-12">
          {title}
        </div>
      </div>
      <div className="col-md-12 search">
        <div className="row">
          <div className="col-md-12 search-form">
            <Accordion id={"tripsSearch"} className='uib-accordion trips-search-accordion' panels={[{
              id: 'parent',
              title: "TRIP SEARCH CRITERIA",
              show: openAccordion,
              children: <TripsTemplate getWaiverData={(waiver: any) => setWaiver(waiver)}
                advFormData={loadTripsForm((paramsObject()))} searchApply={searchApply}></TripsTemplate>
            } as AccordionPanelProps]} />
          </div>
        </div>
      </div>
      <div className="row search-results">
        <div className="col-md-12">
          {hasTrips() ? <div className="total-found">
            {searchWaiverName && <div className="waiver-results">
              {searchWaiverName} waiver matched these trips
            </div>}
            <span>DISPLAYING 1-{trips.length} of {totalTripsFound}</span> 
            {hasMoreTrips() && <button
              type="button"
              className="btn btn-default button-action"
              onClick={showMoreTrips}
            >
              SHOW MORE
            </button>}
          </div> : <div className="total-found">
            <span>NO ITEMS TO DISPLAY</span>
          </div>}
          <div className="table-responsive">
            <table className="table table-striped table-hover search-results-table">
              <thead>
                <tr>
                  {headerLists.map((line, index) => {
                    let className = (isSort(line.keyName) ? ' sort ' : ' ');
                    if (line.keyName === 'record') {
                      className += " top-left-header-cell ";
                    } else if (line.keyName === 'arrival') {
                      className += " top-right-header-cell ";
                    }
                    if (line.template) {
                      return <React.Fragment key={index}>{line.template}</React.Fragment>;
                    }
                    return <th key={index}
                      className={className}
                      onClick={() => {
                        if (line.keyName) {
                          sortBy(line.keyName);
                        }
                      }}
                    >
                      {line.displayName}
                      <i
                        className={`sprites ${
                          isSort(line.keyName, 'asc')
                            ? 'sprite-arrow-orange-up'
                            : isSort(line.keyName, 'desc')
                            ? 'sprite-arrow-orange-down'
                            : ''
                        }`}
                      ></i>
                    </th>
                  })}
                </tr>
              </thead>
              {hasTrips() && <tbody>
                <TripsListItem trips={trips} />
              </tbody>}
            </table>
          </div>
          {hasTrips() && <>
            <div className="separator"></div>
            <div className="total-found">
              <span>DISPLAYING 1-{trips.length} of {totalTripsFound}</span>
              {hasMoreTrips() && <button
                type="button"
                className="btn btn-default button-action"
                onClick={showMoreTrips}
              >
                SHOW MORE
              </button>}
            </div>
          </>}
        </div>
        {!hasTrips() && <div className="row">
          <div className="col-md-12">
            <div className="col-md-12 no-search-results">
              {noResultsMessage()}
            </div>
          </div>
        </div>}
        {error && <div className="row">
          <div className="col-md-12">
            <div className="col-md-12 error">
              {error.title} - {error.message}
            </div>
          </div>
        </div>}
      </div>
    </div>
  );
};

export default Trips;